import React, { ChangeEvent } from "react";
import { Fieldset } from "../../Components/Fieldset";
import { TextInput } from "../../Components/TextInput";
import { Error } from "../../Components/Error";
import { sanitiseString } from "../../Utils/StringUtils";

export interface Props {
  id: string;
  dataTestId: string;
  legend: string;
  legendClasses?: string;
  legendIsQuestionTitle?: boolean;
  description: string;
  classes?: string;
  day?: DateField;
  month: DateField;
  year?: DateField;
  dontWrap?: boolean;
  error?: React.ComponentProps<typeof Error>;
}

export interface DateField {
  label: { text: string; description?: string };
  name: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>, ...args: unknown[]) => void;
  error?: boolean;
}

export const DatesInput = (props: Props) => {
  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    dateType: "day" | "month" | "year"
  ) => {
    if (
      dateType === "day" &&
      props.day !== undefined &&
      props.day.onChange !== undefined
    ) {
      props.day.onChange(e, e.target.value);
    } else if (
      dateType === "month" &&
      props.month !== undefined &&
      props.month.onChange !== undefined
    ) {
      props.month.onChange(e, e.target.value);
    } else if (
      dateType === "year" &&
      props.year !== undefined &&
      props.year.onChange !== undefined
    ) {
      props.year.onChange(e, e.target.value);
    }
  };

  const renderDatesInput = () => {
    const classesDay: string =
      (props.error && props.day?.error) ||
      (props.error &&
        !props.day?.error &&
        !props.month.error &&
        !props.year?.error)
        ? "ons-input--error"
        : "";

    const classesMonth: string =
      (props.error && props.month.error) ||
      (props.error &&
        !props.day?.error &&
        !props.month.error &&
        !props.year?.error)
        ? "ons-input--error"
        : "";

    const classesYear: string =
      (props.error && props.year?.error) ||
      (props.error &&
        !props.day?.error &&
        !props.month.error &&
        !props.year?.error)
        ? "ons-input--error"
        : "";

    const fields = (
      <div className="ons-field-group">
        {props.day !== undefined && (
          <TextInput
            id={`${sanitisedId}-day`}
            label={{
              text: props.day.label.text,
              description: props.day.label.description,
              id: `${sanitisedId}-day-label`,
            }}
            onChange={(e) => handleChange(e, "day")}
            width="2"
            min={1}
            max={31}
            maxLength={2}
            type="number"
            value={props.day?.value}
            name={props.day?.name}
            classes={classesDay}
            dataTestId={`${sanitisedDataTestId}-day`}
          />
        )}

        <TextInput
          id={`${sanitisedId}-month`}
          label={{
            text: props.month.label.text,
            description: props.month.label.description,
            id: `${sanitisedId}-month-label`,
          }}
          onChange={(e) => handleChange(e, "month")}
          width="2"
          min={1}
          max={12}
          maxLength={2}
          type="number"
          value={props.month.value}
          name={props.month.name}
          classes={classesMonth}
          dataTestId={`${sanitisedDataTestId}-month`}
        />

        {props.year !== undefined && (
          <TextInput
            id={`${sanitisedId}-year`}
            label={{
              text: props.year.label.text,
              description: props.year.label.description,
              id: `${sanitisedId}-year-label`,
            }}
            onChange={(e) => handleChange(e, "year")}
            width="4"
            min={1000}
            max={3000}
            maxLength={4}
            type="number"
            value={props.year?.value}
            name={props.year?.name}
            classes={classesYear}
            dataTestId={`${sanitisedDataTestId}-year`}
          />
        )}
      </div>
    );

    return (
      <Fieldset
        id={sanitisedId}
        classes={props.classes}
        legend={props.legend}
        legendClasses={props.legendClasses}
        description={props.description}
        dontWrap={props.dontWrap}
        legendIsQuestionTitle={props.legendIsQuestionTitle}
        error={props.error}
        dataTestId={`fieldset-${sanitisedDataTestId}`}
      >
        {fields}
      </Fieldset>
    );
  };

  return renderDatesInput();
};
